<template>
    <div class="rsvp-wrapper">
        <div class="rsvp-card">
            <!-- Edit toolbar (top of card, only in edit mode) -->
            <div v-if="editMode && isStandalone" class="rsvp-edit-toolbar">
                <button
                    class="rsvp-edit-toolbar__btn"
                    :class="{ 'rsvp-edit-toolbar__btn--active': isModalOpen }"
                    @click.stop="isModalOpen = !isModalOpen"
                >
                    <font-awesome-icon icon="fa-solid fa-sliders" />
                    <span>Campuri</span>
                </button>
                <div class="rsvp-edit-toolbar__btn rsvp-edit-toolbar__btn--color" @click.stop>
                    <span
                        class="rsvp-edit-toolbar__color-chip"
                        :style="{ backgroundColor: section.backgroundColor.value }"
                    ></span>
                    <input
                        type="color"
                        :value="section.backgroundColor.value"
                        class="rsvp-edit-toolbar__color-input"
                        @input="updateBgColor"
                    />
                    <span>Fundal</span>
                </div>
            </div>
            <!-- Legacy Campuri button (non-standalone) -->
            <button
                v-if="editMode && !isStandalone"
                class="fab-edit-toggle"
                style="position: absolute; right: 12px; top: 12px; z-index: 5;"
                @click.stop="isModalOpen = true"
            >
                <font-awesome-icon icon="fa-solid fa-sliders" />
                <span>Campuri</span>
            </button>

            <Teleport to="body">
                <!-- Action sheet overlay -->
                <transition name="action-sheet-fade">
                    <div
                        v-if="isModalOpen"
                        class="action-sheet-overlay"
                        @click.stop="closeModal"
                    ></div>
                </transition>

                <!-- Action sheet panel for field toggles -->
                <transition name="action-sheet-slide">
                    <div
                        v-if="isModalOpen"
                        class="action-sheet"
                        @click.stop
                    >
                        <div class="action-sheet__header">
                            <span class="action-sheet__title">Modifica Formular</span>
                            <button class="action-sheet__close" @click.stop="closeModal">
                                <font-awesome-icon icon="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <div class="rsvp-field-toggles">
                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Numar Adulti</span>
                                <input
                                    v-model="newSection.fields.adultCount"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>

                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Numar Copii</span>
                                <input
                                    v-model="newSection.fields.childrenCount"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>

                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Tip Meniu</span>
                                <input
                                    v-model="newSection.fields.foodMenu"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>

                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Meniu Copii</span>
                                <input
                                    v-model="newSection.fields.childrenMenu"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>

                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Cazare</span>
                                <input
                                    v-model="newSection.fields.accomodation"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>

                            <div class="rsvp-field-toggle">
                                <span class="rsvp-field-toggle__label">Mesaj</span>
                                <input
                                    v-model="newSection.fields.message"
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                />
                            </div>
                        </div>

                        <div style="padding: 0 20px 8px;">
                            <button
                                class="rsvp-field-save-btn"
                                @click.stop="saveEdit(true)"
                            >
                                Salveaza
                            </button>
                        </div>
                    </div>
                </transition>
            </Teleport>

            <!-- ── Integrated header (standalone only) ── -->
            <div v-if="isStandalone" class="rsvp-header">
                <p
                    class="rsvp-header__title"
                    :class="{ 'rsvp-header__text--editable': editMode }"
                    :style="{
                        fontFamily: section.title.fontFamily._value,
                        color: section.title.textColor.value,
                    }"
                    v-html="section.title.text.value.replace(/\n/g, '<br>')"
                    @click.stop="onHeaderClick('title')"
                ></p>
                <SectionTextEditBar
                    ref="headerTitleBar"
                    :textValue="section.title.text.value"
                    :colorValue="section.title.textColor.value"
                    :sizeValue="section.title.fontSize.value"
                    :shadowValue="section.title.text.textShadow"
                    :marginValue="section.title.marginTop.value"
                    :fontValue="section.title.fontFamily._value"
                    :fonts="fonts"
                    :section="section"
                    :sectionKey="sectionKey"
                    textPropertyKey="title.text.value"
                    colorPropertyKey="title.textColor.value"
                    sizePropertyKey="title.fontSize.value"
                    shadowPropertyKey="title.text.textShadow"
                    marginPropertyKey="title.marginTop.value"
                    fontPropertyKey="title.fontFamily._value"
                    :marginConfig="section.title.marginTop._config || {}"
                    :compact="true"
                    @update-section="emitUpdateSection"
                />
                <p
                    class="rsvp-header__desc"
                    :class="{ 'rsvp-header__text--editable': editMode }"
                    :style="{
                        fontFamily: section.description.fontFamily._value,
                        color: section.description.textColor.value,
                    }"
                    v-html="section.description.text.value.replace(/\n/g, '<br>')"
                    @click.stop="onHeaderClick('desc')"
                ></p>
                <SectionTextEditBar
                    ref="headerDescBar"
                    :textValue="section.description.text.value"
                    :colorValue="section.description.textColor.value"
                    :sizeValue="section.description.fontSize.value"
                    :shadowValue="section.description.text.textShadow"
                    :marginValue="section.description.marginTop.value"
                    :fontValue="section.description.fontFamily._value"
                    :fonts="fonts"
                    :section="section"
                    :sectionKey="sectionKey"
                    textPropertyKey="description.text.value"
                    colorPropertyKey="description.textColor.value"
                    sizePropertyKey="description.fontSize.value"
                    shadowPropertyKey="description.text.textShadow"
                    marginPropertyKey="description.marginTop.value"
                    fontPropertyKey="description.fontFamily._value"
                    :marginConfig="section.description.marginTop._config || {}"
                    :compact="true"
                    @update-section="emitUpdateSection"
                />
            </div>

            <!-- ── Form ── -->
            <form v-if="!isButtonDisabled" @submit.prevent class="rsvp-form">
                <!-- Name row -->
                <div class="rsvp-row">
                    <div class="rsvp-field">
                        <label class="rsvp-field__label" for="surname">
                            Nume
                            <font-awesome-icon v-if="isNameLocked" icon="fa-solid fa-lock" class="rsvp-field__lock" />
                        </label>
                        <div class="rsvp-field__input-wrap">
                            <font-awesome-icon icon="fa-solid fa-user" class="rsvp-field__icon" />
                            <input
                                v-model="surname"
                                @change="handleInputChange()"
                                id="surname"
                                name="surname"
                                type="text"
                                autocomplete=""
                                required
                                requiredForced
                                class="rsvp-field__input"
                                :class="{ 'rsvp-field__input--locked': isNameLocked }"
                                :readonly="isNameLocked"
                                placeholder="Numele tau"
                            />
                        </div>
                    </div>

                    <div class="rsvp-field">
                        <label class="rsvp-field__label" for="name">
                            Prenume
                            <font-awesome-icon v-if="isNameLocked" icon="fa-solid fa-lock" class="rsvp-field__lock" />
                        </label>
                        <div class="rsvp-field__input-wrap">
                            <font-awesome-icon icon="fa-solid fa-user" class="rsvp-field__icon" />
                            <input
                                v-model="name"
                                @change="handleInputChange()"
                                id="name"
                                name="name"
                                type="text"
                                autocomplete=""
                                required
                                requiredForced
                                class="rsvp-field__input"
                                :class="{ 'rsvp-field__input--locked': isNameLocked }"
                                :readonly="isNameLocked"
                                placeholder="Prenumele tau"
                            />
                        </div>
                    </div>
                </div>

                <!-- Adults / Children row -->
                <Transition name="rsvp-field-fade">
                    <div v-if="newSection.fields.adultCount || newSection.fields.childrenCount" class="rsvp-row">
                        <div v-if="newSection.fields.adultCount" class="rsvp-field">
                            <label class="rsvp-field__label" for="adults">Adulti</label>
                            <div class="rsvp-field__input-wrap">
                                <font-awesome-icon icon="fa-solid fa-users" class="rsvp-field__icon" />
                                <input
                                    v-model="adults"
                                    @change="handleInputChange()"
                                    id="adults"
                                    name="adults"
                                    type="number"
                                    required
                                    autocomplete=""
                                    min="1"
                                    class="rsvp-field__input"
                                    placeholder="1"
                                />
                            </div>
                        </div>

                        <div v-if="newSection.fields.childrenCount" class="rsvp-field">
                            <label class="rsvp-field__label" for="children">Copii</label>
                            <div class="rsvp-field__input-wrap">
                                <font-awesome-icon icon="fa-solid fa-child" class="rsvp-field__icon" />
                                <input
                                    v-model="children"
                                    id="children"
                                    name="children"
                                    type="number"
                                    min="0"
                                    autocomplete=""
                                    class="rsvp-field__input"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- Food menu -->
                <Transition name="rsvp-field-fade">
                    <div v-if="newSection.fields.foodMenu" class="rsvp-row">
                        <div class="rsvp-field rsvp-field--full">
                            <label class="rsvp-field__label">Meniu</label>
                            <div class="rsvp-field__input-wrap">
                                <font-awesome-icon icon="fa-solid fa-utensils" class="rsvp-field__icon" />
                                <select
                                    id="menuTypeSelect"
                                    v-model="selectedMenuType"
                                    @change="handleInputChange(selectedMenuType)"
                                    class="rsvp-field__input rsvp-field__select"
                                >
                                    <option
                                        v-for="menuType in menuTypes"
                                        :key="menuType.value"
                                        :value="menuType.value"
                                    >
                                        {{ menuType.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- Children menu -->
                <Transition name="rsvp-field-fade">
                    <div v-if="newSection.fields.childrenMenu" class="rsvp-row">
                        <div class="rsvp-field rsvp-field--full">
                            <label class="rsvp-field__label">Meniu pentru copii?</label>
                            <div class="rsvp-pill-group">
                                <label class="rsvp-pill" :class="{ 'rsvp-pill--active': childrenMenu === 'true' }">
                                    <input v-model="childrenMenu" type="radio" name="childrenMenu" value="true" class="rsvp-pill__input" />
                                    Da
                                </label>
                                <label class="rsvp-pill" :class="{ 'rsvp-pill--active': childrenMenu === 'false' }">
                                    <input v-model="childrenMenu" type="radio" name="childrenMenu" value="false" class="rsvp-pill__input" />
                                    Nu
                                </label>
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- Accommodation -->
                <Transition name="rsvp-field-fade">
                    <div v-if="newSection.fields.accomodation" class="rsvp-row">
                        <div class="rsvp-field rsvp-field--full">
                            <label class="rsvp-field__label">Doresti cazare?</label>
                            <div class="rsvp-pill-group">
                                <label class="rsvp-pill" :class="{ 'rsvp-pill--active': accommodationChoice === '1' }">
                                    <input v-model="accommodationChoice" type="radio" value="1" name="accomodation" class="rsvp-pill__input" />
                                    Da
                                </label>
                                <label class="rsvp-pill" :class="{ 'rsvp-pill--active': accommodationChoice === '0' }">
                                    <input v-model="accommodationChoice" type="radio" value="0" name="accomodation" class="rsvp-pill__input" />
                                    Nu
                                </label>
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- Message -->
                <Transition name="rsvp-field-fade">
                    <div v-if="newSection.fields.message" class="rsvp-row">
                        <div class="rsvp-field rsvp-field--full">
                            <label class="rsvp-field__label" for="message">Mesaj</label>
                            <div class="rsvp-field__input-wrap rsvp-field__input-wrap--textarea">
                                <font-awesome-icon icon="fa-solid fa-comment" class="rsvp-field__icon rsvp-field__icon--textarea" />
                                <textarea
                                    v-model="message"
                                    id="message"
                                    class="rsvp-field__input rsvp-field__textarea"
                                    placeholder="Scrie-ne un mesaj..."
                                    rows="3"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- Action buttons -->
                <div class="rsvp-actions">
                    <button
                        @click.prevent="() => submitResponse('submitNegative')"
                        type="button"
                        class="rsvp-btn rsvp-btn--outline"
                    >
                        Nu pot participa
                    </button>
                    <button
                        @click.prevent="() => submitResponse('submitPozitive')"
                        type="button"
                        class="rsvp-btn rsvp-btn--primary"
                    >
                        <font-awesome-icon icon="fa-solid fa-check" />
                        Confirm prezenta
                    </button>
                </div>
            </form>

            <!-- ── Submitted state ── -->
            <div v-if="isButtonDisabled" class="rsvp-submitted-state">
                <div class="rsvp-submitted-icon">
                    <font-awesome-icon icon="fa-solid fa-circle-check" />
                </div>
                <p class="rsvp-submitted-text">Raspunsul tau a fost inregistrat.</p>
                <p v-if="type === 'complex'" class="rsvp-submitted-hint">Daca doresti sa modifici raspunsul, contacteaza organizatorul evenimentului.</p>
                <!--
                <button v-if="type !== 'complex'" class="rsvp-resubmit-btn" @click="showFormAgain">
                    <font-awesome-icon icon="fa-solid fa-pen" class="mr-2" />
                    Trimite un nou raspuns
                </button>
                -->
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import SectionTextEditBar from '@/components/editorUtilsInline/SectionTextEditBar.vue';

export default {
    components: {
        SectionTextEditBar,
    },
    data() {
        return {
            name: '',
            surname: '',
            adults: 1,
            children: 0,
            accommodationChoice: '',
            selectedMenuType: 'standard',
            menuTypes: [
                { value: 'standard', label: 'Standard' },
                { value: 'vegetarian', label: 'Vegetarian' },
            ],
            message: '',
            childrenMenu: false,
            type: null,
            show: {
                menu: true,
                accommodation: true,
                childrenMenu: true,
            },
            isButtonDisabled: false,
            cookieName: 'cta_disabled',
            isEditing: false,
            editedValue: this.value,
            isModalOpen: false,
            newSection: {
                fields: {
                    foodMenu: false,
                    childrenMenu: false,
                    accomodation: false,
                    adultCount: true,
                    childrenCount: true,
                    message: true,
                },
            },
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
        isNameLocked() {
            const n = (this.recipientName || '').trim();
            const s = (this.recipientSurname || '').trim();
            return this.type === 'complex' && (!!n || !!s);
        },
    },
    props: {
        projectId: {
            required: true,
        },
        invitationId: {
            required: true,
        },
        isOwner: {
            type: Boolean, // Adjust the type if it's not a string
            required: true,
        },
        section: {
            type: Object,
            required: true,
        },
        sectionKey: {
            type: String,
            required: true,
        },
        config: {
            required: false,
        },
        recipientName: {
            type: String,
            default: '',
        },
        recipientSurname: {
            type: String,
            default: '',
        },
        hasResponded: {
            type: Boolean,
            default: false,
        },
        formUnlocked: {
            type: Boolean,
            default: false,
        },
        isStandalone: {
            type: Boolean,
            default: false,
        },
        fonts: {
            type: Array,
            default: () => [],
        },
        /** Mirrors router `isSimple` — both `/ps/:id` and `/ps/:id/:slug` are simple */
        isSimple: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update-section', 'open-text-editor'],
    mounted() {
        // Attach event listener to clear error class on input change
        const requiredElements = this.$el.querySelectorAll('[required]');
        requiredElements.forEach((element) => {
            element.addEventListener('input', this.clearErrorClass);
        });

        let newSectionPrep = this.section;

        if (newSectionPrep.fields == undefined) {
            newSectionPrep.fields = this.newSection.fields;
        }

        this.newSection = newSectionPrep;
    },

    beforeUnmount() {
        // Remove event listener when component is about to be destroyed
        const requiredElements = this.$el.querySelectorAll('[required]');
        requiredElements.forEach((element) => {
            element.removeEventListener('input', this.clearErrorClass);
        });
    },
    setup() {
        // Get toast interface
        const toast = useToast();

        // Make it available inside methods
        return { toast };
    },
    created() {
        // Check if the user is already authenticated (e.g., on page reload)
        this.isAuthenticated = this.checkAuthentication();
        // You may want to add a global event listener to listen for authentication changes
        window.addEventListener('storage', this.handleStorageEvent);

        this.type = this.isSimple ? 'simple' : 'complex';

        // Pre-fill name/surname for complex invitations
        if (this.type === 'complex') {
            if (this.recipientName) this.name = this.recipientName;
            if (this.recipientSurname) this.surname = this.recipientSurname;

            // Server-side lock: if already responded and form not unlocked, disable form
            if (this.hasResponded && !this.formUnlocked) {
                this.isButtonDisabled = true;
            }
        }

        this.checkCookie(); // Check the cookie when the component is created
    },
    methods: {
        onHeaderClick(which) {
            if (!this.editMode) return;
            const ref = which === 'title' ? this.$refs.headerTitleBar : this.$refs.headerDescBar;
            if (ref && ref.open) ref.open();
        },
        emitUpdateSection(sectionKey, sectionData) {
            this.$emit('update-section', sectionKey, sectionData);
        },
        updateBgColor(event) {
            const updated = { ...this.section };
            updated.backgroundColor = { ...updated.backgroundColor, value: event.target.value };
            this.$emit('update-section', this.sectionKey, updated);
        },
        openModal() {
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        handleInputChange() {
            // Handle changes when the select element value changes
            // console.log('Selected:', element);
            // element.classList.add('border-green-600');
        },
        saveEdit(closeModal) {
            if (closeModal) {
                this.closeModal();
            }
            this.isEditing = false;
            this.$emit(
                'update-section',
                this.sectionKey,
                this.newSection,
                true
            );
        },
        clearErrorClass(event) {
            // Clear the error class on input change
            const element = event.target;
            element.classList.remove('border-red-600');
        },
        async submitResponse(buttonType) {
            let willParticipate = true;

            if (buttonType == 'submitNegative') {
                willParticipate = false;

                const requiredElements =
                    this.$el.querySelectorAll('[requiredForced]');

                for (const element of requiredElements) {
                    const value = element.value.trim();

                    if (!value) {
                        // Display an error message
                        this.toast.error(
                            'Unele campuri sunt necesare, va rugam sa completati formularul'
                        );
                        // Add red border class for validation
                        element.classList.add('border-red-600');
                        return; // Prevent further execution
                    }
                }
            }

            if (willParticipate) {
                const requiredElements =
                    this.$el.querySelectorAll('[required]');

                for (const element of requiredElements) {
                    const value = element.value.trim();

                    if (!value) {
                        // Display an error message
                        this.toast.error(
                            'Unele campuri sunt necesare, va rugam sa completati formularul'
                        );
                        // Add red border class for validation
                        element.classList.add('border-red-600');
                        return; // Prevent further execution
                    }
                }
            }

            if (this.isOwner) {
                this.toast.info(
                    'Esti in modul editare, formularul nu a fost trimis.'
                );
                return;
            }

            try {
                // Assuming you have form data available in Vue data properties
                const formData = {
                    name: this.name,
                    surname: this.surname,
                    adults: this.adults,
                    children: this.children,
                    childrenMenu: this.childrenMenu,
                    accommodationChoice: Number(this.accommodationChoice),
                    selectedMenuType: this.selectedMenuType,
                    message: this.message || this.newSection.fields.message,
                    projectId: this.projectId,
                    uniqueParameter: this.invitationId
                        ? this.invitationId
                        : 'simple',
                    willParticipate: willParticipate,
                    type: this.invitationId ? 'complex' : 'simple',
                };

                await axios
                    .post(
                        '/api/index.php?endpoint=addInvitationResponse',
                        formData,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    )
                    .then((result) => {
                        if (result.data.success) {
                            this.toast.success(
                                `Raspunsul tău a fost inregistrat cu succes!`
                            );

                            this.isButtonDisabled = true;
                            if (this.type === 'simple') {
                                this.$cookies.set(this.cookieName, true, 1); // Cookie expires in 1 day (24 hours)
                            }
                        } else {
                            this.toast.error(
                                `A aparut o eroare la trimiterea formularului!`
                            );
                        }
                    })
                    .catch(() => {
                        this.toast.error(
                            `A aparut o eroare la trimiterea formularului!`
                        );
                    });
            } catch (error) {
                console.error(
                    'A aparut o eroare la trimiterea formularului!',
                    error.response ? error.response.data : error.message
                );
            }
        },
        checkCookie() {
            // Check if the cookie exists
            const cookieExists = this.$cookies.get(this.cookieName);
            if (cookieExists) {
                this.isButtonDisabled = true;
            }
        },
        checkAuthentication() {
            // Check if the authentication token is present in localStorage
            const token = localStorage.getItem('userToken');
            return !!token; // Returns true if the token is present, otherwise false
        },
        handleStorageEvent(event) {
            // Listen for changes in localStorage (e.g., logout in another tab)
            if (event.key === 'userToken') {
                this.isAuthenticated = this.checkAuthentication();
            }
        },
        showFormAgain() {
            this.isButtonDisabled = false;
        },
    },
};
</script>

<style scoped>
/* ── Transition for field rows ── */
.rsvp-field-fade-enter-active,
.rsvp-field-fade-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease;
}
.rsvp-field-fade-enter-from,
.rsvp-field-fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
.rsvp-field-fade-enter-to,
.rsvp-field-fade-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>

<style>
/* ══════════════════════════════════════════════════
   RSVP Form — Modern Redesign
   ══════════════════════════════════════════════════ */

/* ── Wrapper ── */
.rsvp-wrapper {
    width: 100%;
    max-width: 500px;
    margin: 40px auto 0;
    padding: 0 16px;
    box-sizing: border-box;
}

/* ── Card ── */
.rsvp-card {
    position: relative;
    background: rgba(255, 255, 255, 0.88);
    backdrop-filter: blur(20px) saturate(1.4);
    -webkit-backdrop-filter: blur(20px) saturate(1.4);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 24px;
    padding: 36px 30px 32px;
    box-shadow:
        0 8px 40px rgba(0, 0, 0, 0.08),
        0 2px 8px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
    overflow: hidden;
}

/* Subtle accent bar at the top of the card */
.rsvp-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #818cf8 0%, #6366f1 40%, #a78bfa 100%);
    border-radius: 24px 24px 0 0;
}

/* ── Edit toolbar (above header, inside card) ── */
.rsvp-edit-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f1f5f9;
    flex-wrap: wrap;
}

.rsvp-edit-toolbar__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    font-family: inherit;
}
.rsvp-edit-toolbar__btn:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #334155;
}
.rsvp-edit-toolbar__btn--active {
    color: #4f46e5;
    background: rgba(99, 102, 241, 0.06);
    border-color: #818cf8;
}

/* Color picker inline */
.rsvp-edit-toolbar__btn--color {
    position: relative;
}
.rsvp-edit-toolbar__color-chip {
    width: 16px;
    height: 16px;
    border-radius: 5px;
    border: 1.5px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;
}
.rsvp-edit-toolbar__color-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    border: none;
    padding: 0;
}

/* ── Integrated header ── */
.rsvp-header {
    text-align: center;
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid #f1f5f9;
}

.rsvp-header__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 8px;
    word-break: break-word;
}

.rsvp-header__desc {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #64748b;
    margin: 0;
    word-break: break-word;
}

/* Edit affordance: dashed outline on hover (desktop) + cursor */
.rsvp-header__text--editable {
    cursor: pointer;
    border-radius: 8px;
    padding: 4px 8px;
    margin-left: -8px;
    margin-right: -8px;
    transition: outline-color 0.15s ease, background 0.15s ease;
    outline: 2px dashed transparent;
}
.rsvp-header__text--editable:hover {
    outline-color: #c7d2fe;
    background: rgba(99, 102, 241, 0.04);
}

@media (max-width: 540px) {
    .rsvp-header {
        margin-bottom: 22px;
        padding-bottom: 20px;
    }
    .rsvp-header__title {
        font-size: 20px;
    }
    .rsvp-header__desc {
        font-size: 13px;
    }
}

/* ── Form layout ── */
.rsvp-form {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.rsvp-row {
    display: flex;
    gap: 14px;
}

@media (max-width: 540px) {
    .rsvp-wrapper {
        margin-top: 28px;
    }
    .rsvp-card {
        padding: 28px 20px 24px;
        border-radius: 20px;
    }
    .rsvp-row {
        flex-direction: column;
        gap: 18px;
    }
}

/* ── Field ── */
.rsvp-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
}
.rsvp-field--full {
    flex: 1 1 100%;
}

/* Label */
.rsvp-field__label {
    font-size: 12px;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
}
.rsvp-field__lock {
    font-size: 10px;
    color: #94a3b8;
}

/* Input wrap (icon + input) */
.rsvp-field__input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.rsvp-field__input-wrap--textarea {
    align-items: flex-start;
}

.rsvp-field__icon {
    position: absolute;
    left: 14px;
    font-size: 13px;
    color: #a1a1aa;
    pointer-events: none;
    z-index: 1;
    transition: color 0.2s ease;
}
.rsvp-field__icon--textarea {
    top: 14px;
}
.rsvp-field__input-wrap:focus-within .rsvp-field__icon {
    color: #6366f1;
}

/* Input */
.rsvp-field__input {
    width: 100%;
    padding: 12px 14px 12px 40px;
    font-size: 15px;
    font-weight: 400;
    color: #1e293b;
    background: rgba(255, 255, 255, 0.65);
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
}
.rsvp-field__input::placeholder {
    color: #a1a1aa;
    font-weight: 400;
}
.rsvp-field__input:hover {
    border-color: #cbd5e1;
    background: rgba(255, 255, 255, 0.8);
}
.rsvp-field__input:focus {
    border-color: #818cf8;
    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.12);
    background: #fff;
}
.rsvp-field__input--locked {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
}
.rsvp-field__input--locked:hover {
    border-color: #e2e8f0;
    background: #f1f5f9;
}
.rsvp-field__input.border-red-600 {
    border-color: #dc2626 !important;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;
}

/* Select */
.rsvp-field__select {
    cursor: pointer;
    padding-right: 36px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M4.47 5.97a.75.75 0 0 1 1.06 0L8 8.44l2.47-2.47a.75.75 0 1 1 1.06 1.06l-3 3a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 0 1 0-1.06z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
}

/* Textarea */
.rsvp-field__textarea {
    resize: vertical;
    min-height: 88px;
    line-height: 1.5;
    padding-top: 12px;
}

/* ── Pill group (radio buttons) ── */
.rsvp-pill-group {
    display: flex;
    gap: 10px;
}
.rsvp-pill {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    background: rgba(255, 255, 255, 0.55);
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}
.rsvp-pill:hover {
    border-color: #cbd5e1;
    background: rgba(255, 255, 255, 0.85);
}
.rsvp-pill--active {
    color: #4f46e5;
    background: rgba(99, 102, 241, 0.08);
    border-color: #818cf8;
    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.08);
}
.rsvp-pill--active:hover {
    background: rgba(99, 102, 241, 0.12);
}
.rsvp-pill__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
}

/* ── Action buttons ── */
.rsvp-actions {
    display: flex;
    gap: 12px;
    padding-top: 10px;
}
@media (max-width: 540px) {
    .rsvp-actions {
        flex-direction: column-reverse;
    }
}

.rsvp-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 600;
    font-family: inherit;
    border-radius: 14px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    line-height: 1.4;
    letter-spacing: 0.1px;
}
.rsvp-btn--primary {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}
.rsvp-btn--primary:hover {
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45);
    transform: translateY(-1px);
}
.rsvp-btn--primary:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}
.rsvp-btn--outline {
    background: rgba(255, 255, 255, 0.5) !important;
    color: #64748b !important;
    border: 1.5px solid #e2e8f0;
}
.rsvp-btn--outline:hover {
    background: rgba(255, 255, 255, 0.85) !important;
    border-color: #cbd5e1;
    color: #334155 !important;
}

/* ── RSVP field toggle rows inside action sheet ── */
.rsvp-field-toggles {
    padding: 0 20px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rsvp-field-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}

.rsvp-field-toggle__label {
    font-size: 14px;
    font-weight: 500;
    color: #334155;
}

.rsvp-field-save-btn {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 10px;
    background: #16a34a;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
    margin-top: 4px;
}
.rsvp-field-save-btn:hover {
    background: #15803d;
}

/* ── Submitted state ── */
.rsvp-submitted-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    text-align: center;
}

.rsvp-submitted-icon {
    font-size: 52px;
    color: #16a34a;
    margin-bottom: 16px;
    animation: rsvp-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes rsvp-pop {
    0% { transform: scale(0); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
}

.rsvp-submitted-text {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 6px;
}
.rsvp-submitted-hint {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 20px;
    line-height: 1.5;
}

.rsvp-resubmit-btn {
    display: inline-flex;
    align-items: center;
    padding: 10px 24px;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.7);
    color: #334155;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}
.rsvp-resubmit-btn:hover {
    background: #fff;
    border-color: #94a3b8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
</style>
